<template>
    <div>
        <b-card>
            <div class="d-flex flex-wrap justify-content-between mt-2">
                <div>
                    <button type="button" class="btn btn-primary mb-2 mr-2 float-left" @click="resetOrderData">
                        <i class="mdi mdi-arrow-left mr-1"></i> Go back
                    </button>
                </div>
                <div v-if="(loggedIn_user_role == 1 || loggedIn_user_role == 2) && hasMounted">
                    <b-button-group class="mb-2">
                        <b-button variant="secondary" @click="printPDF('printCustomerLabel')">
                            <i class="mdi mdi-printer mr-1"></i> Customer Label
                        </b-button>
                        <b-button class="ml-2" variant="secondary" @click="printPDF('printOrder')">
                            <i class="mdi mdi-printer mr-1"></i> Quality Control Form
                        </b-button>
                    </b-button-group>
                </div>
            </div>
        </b-card>

        <b-card v-if="hasMounted" style="min-height: 400px">
            <!-- these compoennts are for printing -->
            <printCustomerLabel v-show="show_printCustomerLabel" />

            <printQualityControlForm v-show="show_printQualityControlForm" />
            <!-- end of printing -->

            <!-- Flag Alert — Resolved -->
            <b-alert v-if="selected_order_details.flagged_for_review && selected_order_details.flag_resolved" show variant="success" class="d-flex align-items-start">
                <i class="mdi mdi-check-circle mdi-24px mr-2"></i>
                <div>
                    <strong>Flag Resolved</strong>
                    <p class="mb-1 mt-1"><strong>Original flag:</strong> {{ selected_order_details.flag_notes }}</p>
                    <p class="mb-1"><strong>Resolution:</strong> {{ selected_order_details.flag_resolution_notes }}</p>
                    <small class="text-muted">
                        Resolved by {{ selected_order_details.resolved_by ? selected_order_details.resolved_by.name : 'Unknown' }}
                        on {{ formatCorrectionDate(selected_order_details.flag_resolved_at) }}
                    </small>
                </div>
            </b-alert>

            <!-- Flag Alert — Open -->
            <b-alert v-else-if="selected_order_details.flagged_for_review" show variant="danger" class="d-flex align-items-start justify-content-between">
                <div class="d-flex align-items-start">
                    <i class="mdi mdi-flag mdi-24px mr-2"></i>
                    <div>
                        <strong>Flagged for Admin Review</strong>
                        <p class="mb-0 mt-1">{{ selected_order_details.flag_notes }}</p>
                    </div>
                </div>
                <b-button v-if="isAdmin" variant="success" size="sm" class="ml-3 flex-shrink-0" @click="showResolveModal = true">
                    <i class="mdi mdi-check mr-1"></i> Resolve Flag
                </b-button>
            </b-alert>

            <!-- Order Source / Payment Info -->
            <b-row class="mb-3">
                <b-col cols="auto">
                    <span class="mr-3">
                        <strong>Source:</strong>
                        <b-badge :variant="selected_order_details.order_source === 'pos' ? 'info' : 'primary'">
                            {{ selected_order_details.order_source === 'pos' ? 'POS' : 'Online' }}
                        </b-badge>
                    </span>
                    <span class="mr-3">
                        <strong>Payment:</strong>
                        <b-badge :variant="paymentBadgeVariant">
                            {{ paymentLabel }}
                        </b-badge>
                    </span>
                    <span v-if="selected_order_details.payment_reference">
                        <strong>Ref:</strong> {{ selected_order_details.payment_reference }}
                    </span>
                </b-col>
                <b-col class="text-right" v-if="canFlagOrder && !selected_order_details.flagged_for_review">
                    <b-button variant="warning" size="sm" @click="showFlagModal = true">
                        <i class="mdi mdi-flag mr-1"></i> Flag for Review
                    </b-button>
                </b-col>
            </b-row>

            <orderTracking v-if="hasMounted" :order_data="selected_order_details" :read_only="!canEditContent" />

            <!-- Correction History -->
            <div v-if="hasMounted && correction_history.length > 0" class="mt-4">
                <b-card no-body>
                    <b-card-header v-b-toggle.correction-history-collapse class="d-flex justify-content-between align-items-center" style="cursor: pointer;">
                        <h5 class="mb-0">
                            <i class="mdi mdi-history mr-1"></i> Correction History
                            <b-badge variant="info" class="ml-1">{{ correction_history.length }}</b-badge>
                        </h5>
                        <i class="mdi mdi-chevron-down"></i>
                    </b-card-header>
                    <b-collapse id="correction-history-collapse" visible>
                        <b-card-body class="p-0">
                            <div class="table-responsive">
                                <table class="table table-sm table-striped mb-0">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Action</th>
                                            <th>Details</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="entry in correction_history" :key="entry.id">
                                            <td>{{ entry.user_name }}</td>
                                            <td>
                                                <b-badge :variant="correctionActionVariant(entry.action)">
                                                    {{ correctionActionLabel(entry.action) }}
                                                </b-badge>
                                            </td>
                                            <td>
                                                <span v-if="entry.changes && entry.changes.before">
                                                    Qty: {{ entry.changes.before.quantity }} → {{ entry.changes.after.quantity }}
                                                </span>
                                            </td>
                                            <td>{{ formatCorrectionDate(entry.created_at) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>
        </b-card>

        <!-- Flag Order Modal -->
        <b-modal v-model="showFlagModal" title="Flag Order for Review" @ok="handleFlagOrder" ok-title="Flag Order" ok-variant="warning" :ok-disabled="!flagNotes.trim()">
            <b-form-group label="Reason for flagging (required):" label-for="flag-notes">
                <b-form-textarea id="flag-notes" v-model="flagNotes" placeholder="Describe the issue with this order..." rows="3" required></b-form-textarea>
            </b-form-group>
        </b-modal>

        <!-- Resolve Flag Modal -->
        <b-modal v-model="showResolveModal" title="Resolve Flag" @ok="handleResolveFlag" ok-title="Resolve" ok-variant="success" :ok-disabled="!resolutionNotes.trim()">
            <p><strong>Original flag notes:</strong> {{ selected_order_details ? selected_order_details.flag_notes : '' }}</p>
            <b-form-group label="Resolution notes (required):" label-for="resolution-notes">
                <b-form-textarea id="resolution-notes" v-model="resolutionNotes" placeholder="Describe how this flag was resolved..." rows="3" required></b-form-textarea>
            </b-form-group>
        </b-modal>

        <!-- <b-overlay no-wrap :show="!hasMounted"></b-overlay> -->
    </div>
</template>

<script>
import OrderTracking from "../ext/track-order.vue";
import { adminComputed, orderComputed, orderMethods } from "@/state/helpers";

//eslint-disable-next-line
import printJS from "print-js";
import * as html2pdf from "../../plugins/html2pdf/html2pdf.bundle.min";
import printCustomerLabel from "./print/customerLabel.vue";
import printQualityControlForm from "./print/qualityControlForm.vue";

export default {
    components: {
        OrderTracking,
        printCustomerLabel,
        printQualityControlForm,
    },
    data: () => ({
        hasMounted: false,
        show_modal: false,
        edit_property_id: -1,
        //these are for the v-show for the printing components
        show_printCustomerLabel: false,
        show_printQualityControlForm: false,
        showFlagModal: false,
        flagNotes: "",
        showResolveModal: false,
        resolutionNotes: "",
    }),
    computed: {
        ...adminComputed,
        ...orderComputed,
        isAdmin() {
            return this.user_role === 'admin';
        },
        canEditContent() {
            if (this.user_role == "admin" || this.user_role == "production") {
                return true;
            }
            return false;
        },
        canFlagOrder() {
            return this.user_role == "admin" || this.user_role == "budtender";
        },
        paymentBadgeVariant() {
            if (this.selected_order_details.payment_method === "cash") return "success";
            if (this.selected_order_details.payment_method === "card") return "warning";
            return "secondary";
        },
        paymentLabel() {
            if (this.selected_order_details.payment_method === "cash") return "Cash";
            if (this.selected_order_details.payment_method === "card") return "Card";
            return "Gateway";
        },
    },
    methods: {
        ...orderMethods,
        correctionActionVariant(action) {
            if (action === 'order_line_added') return 'success';
            if (action === 'order_line_removed') return 'danger';
            return 'warning';
        },
        correctionActionLabel(action) {
            if (action === 'order_line_added') return 'Added';
            if (action === 'order_line_removed') return 'Removed';
            if (action === 'order_line_modified') return 'Modified';
            return action;
        },
        formatCorrectionDate(dateStr) {
            if (!dateStr) return '';
            let d = new Date(dateStr);
            return d.toLocaleDateString('en-ZA') + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
        },
        loadCorrectionHistory() {
            if (this.selected_order_details && this.selected_order_details.id) {
                this.getOrderCorrectionHistory({ order_id: this.selected_order_details.id });
            }
        },
        handleResolveFlag(bvModalEvent) {
            bvModalEvent.preventDefault();
            if (!this.resolutionNotes.trim()) return;
            this.resolveFlag({
                order_id: this.selected_order_details.id,
                resolution_notes: this.resolutionNotes,
            }).then(() => {
                this.showResolveModal = false;
                this.resolutionNotes = "";
                this.getOrderDetails(this.selected_order_details.id);
                this.$bvToast.toast("Flag resolved successfully", {
                    title: "Flag Resolved",
                    variant: "success",
                    autoHideDelay: 3000,
                    solid: true,
                });
            });
        },
        handleFlagOrder(bvModalEvent) {
            bvModalEvent.preventDefault();
            if (!this.flagNotes.trim()) return;
            this.flagOrder({
                order_id: this.selected_order_details.id,
                flag_notes: this.flagNotes,
            }).then(() => {
                this.showFlagModal = false;
                this.flagNotes = "";
                this.getOrderDetails(this.selected_order_details.id);
                this.$bvToast.toast("Order flagged for admin review", {
                    title: "Order Flagged",
                    variant: "warning",
                    autoHideDelay: 3000,
                    solid: true,
                });
            });
        },
        resetOrderData() {
            // this.$router.push({ path: "/clients" });
            //go back to prev route
            this.$router.go(-1);
        },
        isOrderLoaded() {
            // check if no order data exists OR id old order data != the selected order id
            if (this.selected_order_details == null) {
                return false;
            }

            return this.selected_order_details.id == this.selected_order_id;
        },
        //both printing buttons will use this method
        printPDF(elementID) {
            const element = document.getElementById(elementID);
            if (!element) {
                //eslint-disable-next-line
                console.error("Element not found");
                return;
            }
            //will v-show on the screen so that html2pdf can "see" the content to print, we set to false after the pdf is generated and passed to printJS
            elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = true) : (this.show_printQualityControlForm = true);

            const options = {
                margin: [10, 10, 10, 10], // top, left, bottom, right
                //this will be the file name
                filename: "exported.pdf",
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, logging: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            };
            // Extend the options object with the didDrawPage hook
            // options.jsPDF.autoPaging = true;
            // options.jsPDF.didDrawPage = (pdf) => {
            //     // If the current position (pdf.y) is beyond the page's available height,
            //     // insert a page break and reset the y position
            //     if (pdf.y > pdf.internal.pageSize.height - options.margin[2]) {
            //         pdf.addPage();
            //         pdf.y = options.margin[0];
            //     }
            // };

            if (typeof window.chrome !== "undefined") {
                this.$nextTick(() => {
                    html2pdf()
                        .set(options)
                        .from(element)
                        .outputPdf("blob")
                        .then(function (pdfBlob) {
                            const url = URL.createObjectURL(pdfBlob);
                            printJS({
                                printable: url,
                                type: "pdf",
                            });
                        })
                        .catch(function (error) {
                            //eslint-disable-next-line
                            console.error("PDF generation error:", error);
                        });

                    //set so that v-show will be false
                    elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = false) : (this.show_printQualityControlForm = false);
                });
            } else {
                //all other browsers will use this
                this.$nextTick(() => {
                    html2pdf()
                        .set(options)
                        .from(element)
                        .outputPdf("datauristring")
                        .then(function (pdfDataUri) {
                            const win = window.open(pdfDataUri, "_blank");
                            if (!win) {
                                //eslint-disable-next-line
                                console.error("Failed to open window");
                                return;
                            }
                        })
                        .catch(function (error) {
                            //eslint-disable-next-line
                            console.error("PDF generation error:", error);
                        });

                    //set so that v-show will be false
                    elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = false) : (this.show_printQualityControlForm = false);
                });
            }
        },
    },

    mounted() {
        if (this.isOrderLoaded()) {
            this.hasMounted = true;
            this.loadCorrectionHistory();
        } else {
            this.getOrderDetails(this.$route.params.orderid).then(() => {
                this.hasMounted = true;
                this.loadCorrectionHistory();
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.propertyContainer {
    display: flex;
    flex-flow: row wrap;
}
.propertyTile {
    /*background: white;*/
    margin: 0.25rem 0.25rem 0.35rem 0.25rem;
    height: 2.5rem;
    /*width: calc( (100% / 3) - 20px );*/
    width: 12rem;
    padding: 0.5rem;

    /* allow floating top right icon */
    position: relative;
}
.page-break {
    display: block;
    page-break-before: always;
}
.avoid-page-break {
    display: table;
    width: 100%;
}
.avoid-page-break tbody {
    display: table-row-group;
}
.avoid-inside-page-break {
    page-break-inside: avoid;
}
</style>
